import React, { useState } from 'react';
import { Plus, X, User, Users, UserCheck, Info, Baby, PersonStanding } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Service, Barber } from '../../types';

export interface Companion {
  name: string;
  type: 'child' | 'elder';
  serviceId: string;
  barberId: string; // 'any' for automatic assignment
}

interface CompanionSelectorProps {
  services: Service[];
  barbers: Barber[];
  onCompanionsChange: (companions: Companion[]) => void;
  onHasCompanionsChange?: (hasCompanions: boolean) => void;
  maxCompanions?: number;
}

const CompanionSelector: React.FC<CompanionSelectorProps> = ({
  services,
  barbers,
  onCompanionsChange,
  onHasCompanionsChange,
  maxCompanions = 10,
}) => {
  const { t } = useTranslation();
  const [hasCompanions, setHasCompanions] = useState(false);
  const [companions, setCompanions] = useState<Companion[]>([]);

  const handleToggleCompanions = (value: boolean) => {
    setHasCompanions(value);
    onHasCompanionsChange?.(value);
    if (value) {
      // Auto-add the first companion so the user sees the form immediately
      if (companions.length === 0) {
        const firstCompanion: Companion = {
          name: '',
          type: 'child',
          serviceId: services[0]?.serviceId || '',
          barberId: 'any',
        };
        setCompanions([firstCompanion]);
        onCompanionsChange([firstCompanion]);
      }
    } else {
      setCompanions([]);
      onCompanionsChange([]);
    }
  };

  const addCompanion = () => {
    if (companions.length >= maxCompanions) return;

    const newCompanion: Companion = {
      name: '',
      type: 'child',
      serviceId: services[0]?.serviceId || '',
      barberId: 'any',
    };

    const updated = [...companions, newCompanion];
    setCompanions(updated);
    onCompanionsChange(updated);
  };

  const removeCompanion = (index: number) => {
    const updated = companions.filter((_, i) => i !== index);
    setCompanions(updated);
    onCompanionsChange(updated);
  };

  const updateCompanion = (index: number, field: keyof Companion, value: string) => {
    const updated = companions.map((c, i) =>
      i === index ? { ...c, [field]: value } : c
    );
    setCompanions(updated);
    onCompanionsChange(updated);
  };

  return (
    <div className="space-y-4">
      {/* Main Question - Who is this appointment for? */}
      <div className="bg-surface rounded-xl p-5 border border-hairline">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-4 h-4 text-ink-subtle" aria-hidden="true" />
          <h3 className="font-sans text-base font-semibold text-ink">
            {t('booking.companions.whoIsFor', '¿Para quién es el turno?')}
          </h3>
        </div>

        {/* Two clear options */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <button
            type="button"
            onClick={() => handleToggleCompanions(false)}
            className={`p-4 rounded-lg font-medium transition-colors border flex flex-col items-center gap-2 ${
              !hasCompanions
                ? 'bg-accent text-on-accent border-accent'
                : 'bg-surface-2 text-ink-muted border-hairline hover:border-hairline-strong hover:text-ink'
            }`}
          >
            <UserCheck className="w-6 h-6" aria-hidden="true" />
            <span className="text-sm">{t('booking.companions.justMe', 'Solo para mí')}</span>
          </button>
          <button
            type="button"
            onClick={() => handleToggleCompanions(true)}
            className={`p-4 rounded-lg font-medium transition-colors border flex flex-col items-center gap-2 ${
              hasCompanions
                ? 'bg-accent text-on-accent border-accent'
                : 'bg-surface-2 text-ink-muted border-hairline hover:border-hairline-strong hover:text-ink'
            }`}
          >
            <Users className="w-6 h-6" aria-hidden="true" />
            <span className="text-sm">{t('booking.companions.withCompanions', 'Con acompañantes')}</span>
          </button>
        </div>

        {/* Important notice */}
        <div className="bg-surface-2 border border-hairline rounded-lg p-3 flex items-start gap-2">
          <Info className="w-4 h-4 text-ink-subtle flex-shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-sm text-ink-muted">
            <strong className="text-ink font-medium">{t('booking.companions.importantNotice', 'Importante:')}</strong>{' '}
            {t('booking.companions.onlyForDependents', 'Los acompañantes solo pueden ser niños o personas mayores que no puedan usar la app por sí mismos. Los adultos deben reservar su propio turno desde su teléfono.')}
          </p>
        </div>
      </div>

      {/* Companions List */}
      {hasCompanions && (
        <div className="space-y-4">
          {companions.map((companion, index) => (
            <div
              key={index}
              className="bg-surface rounded-xl p-4 border border-hairline"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-ink-subtle" aria-hidden="true" />
                  <span className="font-medium text-ink">
                    {t('booking.companions.companion', 'Acompañante')} {index + 1}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => removeCompanion(index)}
                  className="p-1 text-ink-subtle hover:text-danger hover:bg-surface-2 rounded-lg transition-colors"
                  aria-label={t('common.remove', 'Quitar')}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-ink-muted mb-1">
                    {t('booking.companions.name', 'Nombre')} *
                  </label>
                  <input
                    type="text"
                    value={companion.name}
                    onChange={(e) => updateCompanion(index, 'name', e.target.value)}
                    placeholder={t('booking.companions.namePlaceholder', 'Nombre del acompañante')}
                    className={`w-full px-3 py-2.5 rounded-lg border bg-surface-3 text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-focus focus:border-transparent text-base ${
                      !companion.name.trim()
                        ? 'border-danger/50'
                        : 'border-hairline'
                    }`}
                  />
                  {!companion.name.trim() && (
                    <p className="text-xs text-danger mt-1">
                      {t('booking.companions.nameRequired', 'Escribe el nombre del acompañante')}
                    </p>
                  )}
                </div>

                {/* Type - Only child or elder */}
                <div>
                  <label className="block text-sm font-medium text-ink-muted mb-2">
                    {t('booking.companions.type', 'Tipo de acompañante')}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => updateCompanion(index, 'type', 'child')}
                      className={`p-3 rounded-lg font-medium transition-colors border flex items-center justify-center gap-2 ${
                        companion.type === 'child'
                          ? 'bg-accent text-on-accent border-accent'
                          : 'bg-surface-2 text-ink-muted border-hairline hover:border-hairline-strong hover:text-ink'
                      }`}
                    >
                      <Baby className="w-5 h-5" aria-hidden="true" />
                      <span className="text-sm">{t('booking.companions.typeChild', 'Niño/a')}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => updateCompanion(index, 'type', 'elder')}
                      className={`p-3 rounded-lg font-medium transition-colors border flex items-center justify-center gap-2 ${
                        companion.type === 'elder'
                          ? 'bg-accent text-on-accent border-accent'
                          : 'bg-surface-2 text-ink-muted border-hairline hover:border-hairline-strong hover:text-ink'
                      }`}
                    >
                      <PersonStanding className="w-5 h-5" aria-hidden="true" />
                      <span className="text-sm">{t('booking.companions.typeElder', 'Persona mayor')}</span>
                    </button>
                  </div>
                </div>

                {/* Service */}
                <div>
                  <label className="block text-sm font-medium text-ink-muted mb-1">
                    {t('booking.companions.service', 'Servicio')} *
                  </label>
                  <select
                    value={companion.serviceId}
                    onChange={(e) => updateCompanion(index, 'serviceId', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-hairline bg-surface-3 text-ink focus:ring-2 focus:ring-focus focus:border-transparent"
                  >
                    {services.map((service) => (
                      <option key={service.serviceId} value={service.serviceId}>
                        {service.name} - {(service.price / 100).toFixed(2)}€
                      </option>
                    ))}
                  </select>
                </div>

                {/* Barber */}
                <div>
                  <label className="block text-sm font-medium text-ink-muted mb-1">
                    {t('booking.companions.barber', 'Barbero')}
                  </label>
                  <select
                    value={companion.barberId}
                    onChange={(e) => updateCompanion(index, 'barberId', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-hairline bg-surface-3 text-ink focus:ring-2 focus:ring-focus focus:border-transparent"
                  >
                    <option value="any">
                      {t('booking.companions.anyBarber', 'Cualquier barbero disponible')}
                    </option>
                    {barbers.map((barber) => (
                      <option key={barber.barberId} value={barber.barberId}>
                        {barber.displayName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          ))}

          {/* Add Companion Button */}
          {companions.length < maxCompanions && (
            <button
              type="button"
              onClick={addCompanion}
              className="w-full py-3.5 px-6 border border-dashed border-hairline-strong rounded-xl bg-surface text-ink-muted hover:text-ink hover:border-accent/60 transition-colors flex items-center justify-center gap-2 text-base font-medium"
            >
              <Plus className="w-5 h-5" aria-hidden="true" />
              {t('booking.companions.addCompanion', 'Añadir otro acompañante')}
            </button>
          )}

          {/* Summary */}
          {companions.length > 0 && (
            <div className="bg-surface-2 border border-hairline rounded-lg p-4">
              <p className="text-sm text-ink-muted">
                <strong className="text-ink font-semibold tabular-nums">{companions.length}</strong>{' '}
                {companions.length === 1
                  ? t('booking.companions.summaryOne', 'acompañante añadido')
                  : t('booking.companions.summaryMany', 'acompañantes añadidos')}
                . {t('booking.companions.summaryNotice', 'Las notificaciones llegarán a tu teléfono.')}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CompanionSelector;
